import { InjectionToken } from '@angular/core';

/**
 * Every bridge call either succeeds with a value or fails with a reason.
 *
 * This shape exists to make one specific bug impossible. The system this
 * replaces had `catch { return [] }` around its USB device query, so a thrown
 * exception and "no devices attached" produced identical results — which is why
 * the permission bug took so long to identify. A caller here cannot read a
 * result without deciding what to do about failure.
 */
export type BridgeResult<T = void> =
  | { ok: true; value: T }
  | { ok: false; error: string };

export function bridgeOk<T>(value: T): BridgeResult<T> {
  return { ok: true, value };
}

export function bridgeFail<T = void>(error: string): BridgeResult<T> {
  return { ok: false, error };
}

/** USB device lifecycle, reported by identity rather than by hub slot. */
export type UsbDeviceState =
  | 'ABSENT'
  | 'PRESENT_NO_PERMISSION'
  | 'READY'
  | 'LOST';

export interface StethoscopeStats {
  running: boolean;
  sampleRate: number;
  packetsOk: number;
  packetsDropped: number;
  overruns: number;
  deviceGone: boolean;
}

/**
 * What the web layer may ask the Android host to do.
 *
 * Note what is absent: there is no `setInputDevice`, and no way to ask for the
 * stethoscope as a capture device. That mirrors §4.1's rule that the native
 * audio manager owns *output only* — the web side must not be able to steer
 * capture, because steering capture is what rebuilds the track.
 */
export interface NativeBridge {
  /** False in a plain browser, where the mock stands in. */
  isNative(): boolean;

  /**
   * Begin a session (§10A.4). Must be safe to call on a dirty slate: the native
   * capture state is process-global, so a previous session's residue outlives
   * the Activity and has to be cleared unconditionally rather than conditionally.
   */
  startSession(sessionId: string): Promise<BridgeResult>;

  /** Ordered, idempotent teardown (§10A.2). */
  endSession(reason: string): Promise<BridgeResult>;

  /** §3.5 step 2 — pin capture to a built-in device. */
  selectBuiltInCommDevice(): Promise<BridgeResult>;

  /** §3.5 step 3 — wait for the route change to take effect. */
  awaitRouteSettled(timeoutMs: number): Promise<BridgeResult>;

  /** §3.5 step 7 — output only, applied once, never re-applied. */
  pinOutputToHdmi(): Promise<BridgeResult>;

  startStethoscope(): Promise<BridgeResult>;
  stopStethoscope(): Promise<BridgeResult>;
  stethoscopeStats(): Promise<BridgeResult<StethoscopeStats>>;

  /**
   * Tell the host the page has joined and is ready for audio.
   *
   * Today's APK gates every PCM buffer on this, silently — miss it and the
   * stethoscope appears broken with nothing in the logs to say why.
   */
  notifyJoined(): Promise<BridgeResult>;

  /**
   * Binary PCM transport (D3). Fires only where the host supports it; callers
   * fall back to the legacy string path when it never fires.
   */
  onPcmPort(handler: (port: MessagePort) => void): void;

  onDeviceState(handler: (state: UsbDeviceState) => void): void;

  /**
   * The stethoscope's state right now, asked rather than waited for.
   *
   * `onDeviceState` only reports *changes*. The host announces a stethoscope
   * that is already plugged in while the Activity starts — before this page has
   * loaded and registered its callbacks — so that announcement is lost, and a
   * page that only listened would believe the device absent for the whole call.
   * Asking once when listening begins closes that gap.
   */
  currentStethoscopeState(): Promise<BridgeResult<UsbDeviceState>>;

  /** Errors raised by the host — surfaced, never swallowed. */
  onStethoscopeError(handler: (message: string) => void): void;

  /**
   * The host lost and restored the audio output route.
   *
   * Reported rather than silently handled: restoring the route is the one
   * operation D14 warns can rebuild the capture track, so the page re-reads its
   * microphone binding afterwards instead of assuming it survived.
   */
  onAudioRouteRestored(handler: (message: string) => void): void;

  /** Release every handler this bridge registered (§10A.3). */
  dispose(): void;
}

export const NATIVE_BRIDGE = new InjectionToken<NativeBridge>('NATIVE_BRIDGE');
